import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const listingBoostSlice = createSlice({
    name: "listingBoost",
    initialState: {
        page: 0,
        pageCount: 0,
        totalRow: 0,
        listData: null,
        statsData: null,
        detailData: null,
        exporting: false,
        actionSuccess: false,
        errorDetailData: null,
    },
    reducers: {
        doGetBoostListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload.current_page;
            state.pageCount = action.payload.page_count;
            state.totalRow = action.payload.total_row;
        },
        doGetBoostListFail: (state) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.totalRow = 0;
        },
        doGetBoostStatsSuccess: (state, action) => {
            state.statsData = action.payload;
        },
        doGetBoostStatsFail: (state) => {
            state.statsData = null;
        },
        doGetBoostDetailSuccess: (state, action) => {
            state.detailData = action.payload.boost;
        },
        doGetBoostDetailFail: (state, action) => {
            state.detailData = null;
            state.errorDetailData = action.payload;
        },
        doCancelBoostSuccess: (state) => {
            state.actionSuccess = true;
        },
        doCancelBoostFail: (state, action) => {
            state.actionSuccess = false;
            state.errorDetailData = action.payload;
        },
        setBoostExporting: (state, action) => {
            state.exporting = action.payload;
        },
        resetListingBoostState: (state) => {
            state.actionSuccess = false;
            state.errorDetailData = null;
        },
        resetListingBoostDetailState: (state) => {
            state.detailData = null;
            state.actionSuccess = false;
            state.errorDetailData = null;
        },
    },
});

const buildBoostListUrl = (param) => {
    let url = `/api/admin/listing-boost?page=${param.page}&size=${param.limit}`;
    if (param.keyword) url += `&keyword=${encodeURIComponent(param.keyword)}`;
    if (param.package_id) url += `&package_id=${param.package_id}`;
    if (param.status) url += `&status=${param.status}`;
    if (param.date_from) url += `&date_from=${param.date_from}`;
    if (param.date_to) url += `&date_to=${param.date_to}`;
    if (param.sort_by) url += `&sort_by=${param.sort_by}`;
    return url;
};

export const doGetBoostList = (param) => async (dispatch) => {
    try {
        const response = await axios.get(buildBoostListUrl(param));
        dispatch(doGetBoostListSuccess(response.data));
    } catch (error) {
        dispatch(doGetBoostListFail(error.response));
    }
};

export const doGetBoostStats = () => async (dispatch) => {
    try {
        const response = await axios.get("/api/admin/listing-boost/stats");
        dispatch(doGetBoostStatsSuccess(response.data));
    } catch (error) {
        dispatch(doGetBoostStatsFail(error.response));
    }
};

export const doGetBoostDetail = (boostId) => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/listing-boost/${boostId}`);
        dispatch(doGetBoostDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetBoostDetailFail(error.response));
    }
};

export const doCancelBoost = (boostId, reason) => async (dispatch) => {
    try {
        const response = await axios.patch(`/api/admin/listing-boost/${boostId}/cancel`, { reason: reason || "" });
        dispatch(doCancelBoostSuccess(response.data));
    } catch (error) {
        dispatch(doCancelBoostFail(error.response));
    }
};

// ---- CSV export --------------------------------------------------------
// There is no server-side export endpoint, so the whole filtered set is pulled
// through the list endpoint (which caps `size` at 100) and the CSV is built in
// the browser — same approach as the payout control export.
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 50;

const csvDate = (dt) => {
    if (!dt) return "";
    const d = new Date(dt);
    if (isNaN(d)) return "";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const STATE_LABEL = {
    ACTIVE: "Active",
    EXPIRING_SOON: "Expiring Soon",
    EXPIRED: "Expired",
    CANCELLED: "Cancelled",
};

export const doExportBoosts = (param) => async (dispatch) => {
    try {
        dispatch(setBoostExporting(true));

        const rows = [];
        for (let page = 1; page <= EXPORT_MAX_PAGES; page++) {
            const url = buildBoostListUrl({ ...param, page, limit: EXPORT_PAGE_SIZE });
            const response = await axios.get(url);
            const batch = response.data?.data || [];
            rows.push(...batch);
            if (batch.length < EXPORT_PAGE_SIZE) break;
        }

        const header = [
            "Transaction ID", "Host", "Host Email", "Listing Title", "Boost Package",
            "Status", "Start Date", "End Date", "Amount", "Payment Method", "Total Boost Score",
        ];
        const body = rows.map((r) => [
            r.transaction_ref, r.host_name, r.host_email, r.listing_title, r.package_name,
            STATE_LABEL[r.state] || r.state, csvDate(r.starts_at), csvDate(r.expires_at),
            r.amount, r.payment_method, r.boost_score == null ? "" : `${r.boost_score} / 100`,
        ]);
        const csv = [header, ...body].map((r) => r.map(csvCell).join(",")).join("\r\n");

        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        const now = new Date();
        link.setAttribute("download", `listing_boosts_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        // no-op: keep the UI responsive even if the export fails
    } finally {
        dispatch(setBoostExporting(false));
    }
};

export const {
    doGetBoostListSuccess, doGetBoostListFail,
    doGetBoostStatsSuccess, doGetBoostStatsFail,
    doGetBoostDetailSuccess, doGetBoostDetailFail,
    doCancelBoostSuccess, doCancelBoostFail,
    setBoostExporting,
    resetListingBoostState, resetListingBoostDetailState,
} = listingBoostSlice.actions;

export const getBoostList = (state) => state.listingBoost?.listData;
export const getBoostListPage = (state) => state.listingBoost?.page;
export const getBoostListPageCount = (state) => state.listingBoost?.pageCount;
export const getBoostListTotalRow = (state) => state.listingBoost?.totalRow;
export const getBoostStats = (state) => state.listingBoost?.statsData;
export const getBoostDetail = (state) => state.listingBoost?.detailData;
export const getBoostExporting = (state) => state.listingBoost?.exporting;
export const getBoostActionSuccess = (state) => state.listingBoost?.actionSuccess;
export const getBoostErrorDetailData = (state) => state.listingBoost?.errorDetailData;

export default listingBoostSlice.reducer;
